import { useState, useEffect, useCallback } from 'react';
import { BookOpen, Plus, Loader2 } from 'lucide-react';
import { dataEngineApi } from '../services/dataEngineApi';
import { SkillList } from '../components/skills/SkillList';
import { SkillEditor } from '../components/skills/SkillEditor';
import { SkillPreview } from '../components/skills/SkillPreview';

const EMPTY_SKILL = { name: '', description: '', content: '', category: '', is_active: true };

export default function SkillsPage() {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full skill object
  const [draft, setDraft] = useState(null);        // editing state
  const [saving, setSaving] = useState(false);

  const fetchSkills = useCallback(async () => {
    try {
      const resp = await dataEngineApi.getSkills();
      setSkills(resp.data || []);
    } catch { setSkills([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchSkills(); }, [fetchSkills]);

  const handleSelect = (skill) => {
    setSelected(skill);
    setDraft({ ...skill });
  };

  const handleNew = () => {
    setSelected(null);
    setDraft({ ...EMPTY_SKILL });
  };

  const handleSave = async () => {
    if (!draft || !draft.name.trim() || !draft.content.trim()) return;
    setSaving(true);
    try {
      if (selected?.id) {
        await dataEngineApi.updateSkill(selected.id, draft);
      } else {
        const resp = await dataEngineApi.createSkill(draft);
        setSelected(resp.data);
      }
      await fetchSkills();
    } catch (err) {
      console.error('Save failed:', err);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selected?.id || !window.confirm(`Delete skill "${selected.name}"?`)) return;
    await dataEngineApi.deleteSkill(selected.id);
    setSelected(null);
    setDraft(null);
    await fetchSkills();
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="h-screen flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-500/10 flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-violet-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight">Skills</h1>
              <p className="text-xs text-gray-500">{skills.length} skills · Agent loads on-demand via progressive disclosure</p>
            </div>
          </div>
          <button
            onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" /> New Skill
          </button>
        </div>

        {/* Three-panel layout */}
        <div className="flex flex-1 overflow-hidden">

          {/* Left — Skill list */}
          <SkillList
            skills={skills}
            loading={loading}
            selectedId={selected?.id}
            onSelect={handleSelect}
          />

          {/* Center — Editor */}
          {draft ? (
            <SkillEditor
              draft={draft}
              onChange={setDraft}
              onSave={handleSave}
              onDelete={selected?.id ? handleDelete : null}
              saving={saving}
              isNew={!selected?.id}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-600">
              <div className="text-center">
                <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Select a skill or create a new one</p>
              </div>
            </div>
          )}

          {/* Right — Preview */}
          {draft && (
            <SkillPreview content={draft.content} name={draft.name} />
          )}
        </div>
      </div>
    </div>
  );
}
